import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

/**
 * GaugeChart - half-circle "toerenteller" gauge using recharts PieChart.
 * Props:
 *  - value: number (current)
 *  - max: number (scale)
 *  - label: string
 *  - displayValue: string (formatted)
 *  - sublabel: optional string
 *  - testid: data-testid
 *  - invert: if true, lower values are "better" (e.g. sales cycle days)
 */
export default function GaugeChart({
  value = 0,
  max = 100,
  label,
  displayValue,
  sublabel,
  testid,
  invert = false,
}) {
  const safeMax = max > 0 ? max : 1;
  const clamped = Math.max(0, Math.min(value, safeMax));
  const percent = clamped / safeMax;

  // Determine color based on performance
  const score = invert ? 1 - percent : percent;
  let needleColor = "#C0C0C0"; // silver default
  if (score >= 0.66) needleColor = "#A7F3D0"; // soft mint
  else if (score >= 0.33) needleColor = "#C0C0C0";
  else needleColor = "#F87171"; // soft red

  // Data: filled portion + remaining
  const data = [
    { name: "filled", value: clamped },
    { name: "remaining", value: safeMax - clamped },
  ];

  return (
    <div
      className="relative bg-[#142539]/80 rounded-xl border border-[rgba(192,192,192,0.15)] p-5 hover:border-[rgba(192,192,192,0.35)] transition-colors overflow-hidden"
      data-testid={testid}
    >
      {/* Subtle gradient accent in corner */}
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full opacity-20"
        style={{
          background: `radial-gradient(circle, ${needleColor} 0%, transparent 70%)`,
        }}
      />

      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#94A3B8] mb-1 font-medium">
          {label}
        </p>

        <div className="relative h-[140px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                cx="50%"
                cy="85%"
                innerRadius={55}
                outerRadius={80}
                startAngle={180}
                endAngle={0}
                paddingAngle={0}
                dataKey="value"
                stroke="none"
                isAnimationActive
                animationDuration={800}
              >
                <Cell fill={needleColor} />
                <Cell fill="rgba(192, 192, 192, 0.1)" />
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {/* Center value overlay */}
          <div className="absolute inset-0 flex flex-col items-center justify-end pb-2 pointer-events-none">
            <p
              className="font-serif-display text-3xl md:text-[2rem] font-semibold text-[#F8FAFC] leading-none"
              data-testid={`${testid}-value`}
            >
              {displayValue}
            </p>
            {sublabel && (
              <p className="text-[11px] text-[#94A3B8] mt-1.5 tracking-wide">{sublabel}</p>
            )}
          </div>
        </div>

        {/* Scale ticks */}
        <div className="flex justify-between text-[9px] uppercase tracking-wider text-[#64748B] mt-1 px-1">
          <span>0</span>
          <span className="text-[#94A3B8] font-medium">
            {Math.round(percent * 100)}%
          </span>
          <span>{max >= 1000 ? `${(max / 1000).toFixed(0)}k` : max}</span>
        </div>
      </div>
    </div>
  );
}
